/**
 * Refinement schema computation (Bug-Fix rewrite).
 *
 * Splits refinement into TWO scopes:
 *
 *   GLOBAL questions   — properties of the transaction itself (what portal /
 *                        subcategory / transaction_type the user is using).
 *                        ONE question per context_type, with the UNION of
 *                        chip values across every relevant rule on every
 *                        card in the wallet. The user picks ONE option;
 *                        engine applies the choice to all cards.
 *
 *   PER-CARD questions — properties of the user's state for a specific card
 *                        (how much have I spent on Magnus this cycle?
 *                        what's my Amex anniversary progress?). Each card
 *                        gets its own independent buckets, so answering for
 *                        Magnus never pollutes Atlas.
 *
 * Why the split: a transaction happens at ONE portal, in ONE subcategory,
 * but each card has INDEPENDENT period_spend and milestone_progress. Mixing
 * them in one map caused the "pick Magnus's cycle-spend → Atlas's cap
 * suddenly says 0 remaining" bug.
 *
 * Outputs:
 *   {
 *     globalQuestions: [
 *       { kind: 'context', context_type: 'portal_channel',
 *         options: [{ id, label, source_card_ids: [...] }, ...] },
 *       ...
 *     ],
 *     perCard: {
 *       [cardId]: { questions: [{ kind: 'cycle_spend' | 'milestone_progress', ... }] }
 *     }
 *   }
 *
 * `aggregateAnswers` is companion-shaped:
 *   { contexts: {...} (global),
 *     per_card_overrides: { [cardId]: { period_used_inr, ..., milestone_progress_inr } } }
 */

const _norm = (s) => (s || '').toString().trim().toLowerCase();

const _formatLakhLabel = (inr) => {
  if (!Number.isFinite(inr) || inr <= 0) return '₹0';
  if (inr >= 100000) return `₹${(inr / 100000).toFixed(inr % 100000 === 0 ? 0 : 1)}L`;
  if (inr >= 1000) return `₹${(inr / 1000).toFixed(inr % 1000 === 0 ? 0 : 1)}k`;
  return `₹${Math.round(inr)}`;
};

/**
 * Does the rule's match clause have any structural reason to fire for the
 * current query? We only check category / merchant / mcc here — context
 * conditions on the rule are evaluated LATER (and may surface as questions).
 */
const _ruleCouldMatch = (rule, query) => {
  const m = rule?.match || {};
  const cats = m.categories || [];
  const merchants = m.merchants || [];
  const mccList = m.mcc || [];
  const hasAnyMatchClause = cats.length + merchants.length + mccList.length > 0;
  if (!hasAnyMatchClause) return true; // catch-all base-earn rules

  const qCat = _norm(query.category);
  const qMerchantSlug = _norm(query.merchant_slug);
  const qMerchantName = _norm(query.merchant);
  const qMcc = _norm(query.mcc);

  if (qCat && cats.some((c) => {
    const cl = _norm(c);
    return cl === qCat || qCat.includes(cl) || cl.includes(qCat);
  })) return true;

  if ((qMerchantSlug || qMerchantName) && merchants.some((mm) => {
    const ml = _norm(mm);
    return ml === qMerchantSlug || ml === qMerchantName;
  })) return true;

  if (qMcc && mccList.some((c) => _norm(c) === qMcc)) return true;
  return false;
};

const _spilloverBuckets = (limitInr) => {
  const limit = Math.max(0, Number(limitInr) || 0);
  if (limit <= 0) return [];
  const half = Math.round(limit * 0.5);
  return [
    { id: 'zero', label: '₹0 (default)',                       assumed_used_inr: 0 },
    { id: 'half', label: `Up to ${_formatLakhLabel(half)}`,    assumed_used_inr: Math.round(half * 0.5) },
    { id: 'near', label: `${_formatLakhLabel(half)} – ${_formatLakhLabel(limit)}`, assumed_used_inr: Math.round(limit * 0.85) },
    { id: 'past', label: `Past ${_formatLakhLabel(limit)} (in spillover)`,        assumed_used_inr: limit + 1 },
  ];
};

const _milestoneBuckets = (thresholdsAsc) => {
  const ts = Array.from(new Set(thresholdsAsc.filter((n) => Number.isFinite(n) && n > 0))).sort((a, b) => a - b);
  if (!ts.length) return [];
  const buckets = [
    { id: 'below_lowest', label: `Below ${_formatLakhLabel(ts[0])} (default)`, assumed_progress_inr: 0 },
  ];
  for (let i = 0; i < ts.length; i++) {
    const t = ts[i];
    const next = ts[i + 1];
    if (next) {
      buckets.push({
        id: `between_${i}`,
        label: `${_formatLakhLabel(t)} – ${_formatLakhLabel(next)}`,
        assumed_progress_inr: Math.round((t + next) / 2),
      });
    } else {
      buckets.push({
        id: `past_${i}`,
        label: `Past ${_formatLakhLabel(t)}`,
        assumed_progress_inr: Math.round(t * 1.05),
      });
    }
  }
  return buckets;
};

/**
 * Look up a friendly label for a registry value. Falls back to the value
 * itself with underscores replaced by spaces + title case if no registry
 * entry matches.
 *
 * @param registry  — { context_type: [{ value, label, ... }, ...] } from
 *                    /api/contexts/list (or equivalent)
 */
const _friendlyLabel = (registry, contextType, value) => {
  const defs = (registry || {})[contextType] || [];
  const hit = defs.find((d) => _norm(d.value) === _norm(value));
  if (hit?.label) return hit.label;
  // Fallback: 'bank_smartbuy' → 'Bank Smartbuy'
  return (value || '')
    .toString()
    .replace(/[_\-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim() || value;
};

/**
 * Build a wallet-wide schema.
 *
 * Returns { globalQuestions, perCard, portalSubcategoryMap }.
 *
 * `portalSubcategoryMap` is a co-occurrence map keyed by portal_channel
 * value → Set of subcategory values used ALONGSIDE that portal by at least
 * one rule. The UI uses it to grey-out / hide subcategory chips that don't
 * apply to the currently-selected portal (strict mode: subcategory is
 * disabled until a portal is picked).
 *
 * @param cards    — array of cards from /api/cards/me (each has .rules, .milestones, .cap_pools)
 * @param query    — { amount, category, merchant_slug, merchant, mcc }
 * @param registry — context_definitions registry for friendly labels
 *                   (optional; falls back to value-derived label if missing)
 */
export const computeWalletRefinement = (cards, query, registry) => {
  const perCard = {};
  // Global aggregation: context_type -> Map(value -> { sourceCardIds: Set })
  const ctxAccumulator = {};
  // Co-occurrence: which subcategory values appear in the same rule as a given
  // portal_channel value. portalValue → Set<subcategoryValue>.
  const portalSubcategoryMap = {};

  for (const card of (cards || [])) {
    const cardId = card.id;
    const matchingRules = (card.rules || []).filter((r) => _ruleCouldMatch(r, query));

    // ── Collect context values across matching rules (GLOBAL) ─────────────
    for (const r of matchingRules) {
      const conds = (r.match?.contexts) || [];
      // First pass — accumulate values per type
      const portalValsInRule = [];
      const subcatValsInRule = [];
      for (const c of conds) {
        const ct = c.context_type;
        const vals = Array.isArray(c.values) ? c.values : [];
        if (!ct || vals.length === 0) continue;
        ctxAccumulator[ct] = ctxAccumulator[ct] || new Map();
        for (const v of vals) {
          if (!v) continue;
          if (!ctxAccumulator[ct].has(v)) {
            ctxAccumulator[ct].set(v, { sourceCardIds: new Set(), sourceRuleIds: new Set() });
          }
          const slot = ctxAccumulator[ct].get(v);
          slot.sourceCardIds.add(cardId);
          slot.sourceRuleIds.add(r.id);
        }
        if (ct === 'portal_channel') portalValsInRule.push(...vals);
        if (ct === 'subcategory') subcatValsInRule.push(...vals);
      }
      // Record portal↔subcategory co-occurrence at the RULE level (same rule
      // = these portal values co-occur with these subcategory values).
      if (portalValsInRule.length && subcatValsInRule.length) {
        for (const pv of portalValsInRule) {
          if (!portalSubcategoryMap[pv]) portalSubcategoryMap[pv] = new Set();
          for (const sv of subcatValsInRule) portalSubcategoryMap[pv].add(sv);
        }
      }
    }

    // ── Per-card questions: cycle-spend (spillover) + milestone ────────────
    const cardQuestions = [];

    // (1) Spillover cap question(s) — at most one per pool/rule key.
    const seenPoolKeys = new Set();
    for (const r of matchingRules) {
      const cap = r.cap;
      if (!cap || cap.spillover_mode !== 'fallback_rule' || !cap.fallback_rule_id) continue;
      let limitInr = cap.amount_inr;
      if (!limitInr && cap.pool_id) {
        const pool = (card.cap_pools || []).find((p) => p.id === cap.pool_id);
        limitInr = pool?.amount_inr;
      }
      if (!limitInr || limitInr <= 0) continue;
      const key = cap.pool_id || r.id;
      if (seenPoolKeys.has(key)) continue;
      seenPoolKeys.add(key);
      const buckets = _spilloverBuckets(limitInr);
      if (!buckets.length) continue;
      cardQuestions.push({
        kind: 'cycle_spend',
        rule_id: r.id,
        rule_label: r.label,
        pool_id: cap.pool_id || null,
        limit_inr: limitInr,
        buckets,
      });
    }

    // (2) Milestone-progress question — at most one per card, grouped.
    const milestones = card.milestones || [];
    const txnAmount = Number(query.amount) || 0;
    if (milestones.length && txnAmount > 0) {
      const thresholds = milestones
        .map((m) => Number(m.threshold_amount_inr || 0))
        .filter((n) => n > 0)
        .sort((a, b) => a - b);
      if (thresholds.length) {
        const smallest = thresholds[0];
        // "Could this transaction realistically cross a milestone?" Heuristic:
        // txn >= 5% of smallest threshold → ask the question. Otherwise we'd
        // be polling the user about a 0.5% probability event.
        if (txnAmount >= smallest * 0.05) {
          const buckets = _milestoneBuckets(thresholds);
          if (buckets.length) {
            cardQuestions.push({
              kind: 'milestone_progress',
              milestone_count: milestones.length,
              thresholds,
              buckets,
            });
          }
        }
      }
    }

    perCard[cardId] = { questions: cardQuestions };
  }

  // ── Materialise global context questions with friendly labels ────────────
  const globalQuestions = [];
  for (const [contextType, valMap] of Object.entries(ctxAccumulator)) {
    const options = [];
    for (const [value, info] of valMap.entries()) {
      options.push({
        id: value,
        label: _friendlyLabel(registry, contextType, value),
        source_card_ids: Array.from(info.sourceCardIds),
        source_rule_ids: Array.from(info.sourceRuleIds),
      });
    }
    // Stable sort: prefer alphabetical by label.
    options.sort((a, b) => a.label.localeCompare(b.label));
    globalQuestions.push({
      kind: 'context',
      context_type: contextType,
      label: _friendlyLabel(registry, '__context_type__', contextType) || contextType,
      options,
    });
  }
  // Stable order: portal_channel first, then subcategory, then others.
  const order = ['portal_channel', 'subcategory', 'transaction_type', 'ecosystem'];
  globalQuestions.sort((a, b) => {
    const ai = order.indexOf(a.context_type); const bi = order.indexOf(b.context_type);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return { globalQuestions, perCard, portalSubcategoryMap };
};

/**
 * Roll up the form state into a /api/recommend payload.
 *
 * @param globalAnswers  — { contexts: { context_type: value, ... } }
 * @param answersByCard  — { [cardId]: { cycle_spend_bucket, cycle_spend_inr,
 *                                       milestone_bucket, milestone_progress_inr } }
 */
export const aggregateAnswers = (globalAnswers, answersByCard) => {
  const contextsOut = {};
  for (const [ct, value] of Object.entries((globalAnswers?.contexts) || {})) {
    if (value) contextsOut[ct] = [value];
  }

  const perCardOverrides = {};
  for (const [cardId, a] of Object.entries(answersByCard || {})) {
    const entry = {};
    if (a?.cycle_spend_inr != null) entry.period_used_inr = a.cycle_spend_inr;
    if (a?.milestone_progress_inr != null) entry.milestone_progress_inr = a.milestone_progress_inr;
    if (Object.keys(entry).length) perCardOverrides[cardId] = entry;
  }

  return {
    contexts: Object.keys(contextsOut).length ? contextsOut : null,
    per_card_overrides: Object.keys(perCardOverrides).length ? perCardOverrides : null,
  };
};
